import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {TuningsService} from "../service/Tunings.service";
import {AllPossibleMatchingModel} from "../models/AllPossibleMatching-model";
import {DownloadFileFromDatabaseService} from "../service/DownloadFileFromDatabase.service";
import {ToastrService} from "ngx-toastr";
import {Location} from "@angular/common";

@Component({
  selector: 'app-all-possible-matchings-table',
  templateUrl: './all-possible-matchings-table.component.html',
  styleUrls: ['./all-possible-matchings-table.component.css']
})
export class AllPossibleMatchingsTableComponent implements OnInit {

  public allPossibleMatchings: AllPossibleMatchingModel[] = [];
  public displayedColumns: (keyof AllPossibleMatchingModel)[] = [];
  public versionId: number;
  public versionName: string;
  public dcf: string;
  public ecuBuild: string;
  public ecuProducer: string;
  private tableName: string = "ALL_POSSIBLE_MATCHING";
  public filesToDownload: number[] = [];
  public sortColumn: keyof AllPossibleMatchingModel | '' = '';
  public sortDirection: 'asc' | 'desc' = 'asc';
  private originalAllPossibleMatchings: AllPossibleMatchingModel[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tuningsService: TuningsService,
    private downloadService: DownloadFileFromDatabaseService,
    private toastr: ToastrService,
    private location: Location
  ) {
    this.route.queryParams.subscribe((params) => {
      this.versionId = params['versionId'];
      this.dcf = params['dcf'];
      this.versionName = params['versionName'];
      this.ecuBuild = params['ecuBuild'];
      this.ecuProducer = params['ecuProducer'];
    })
  }

  ngOnInit() {
    this.getTableHeaderNames();
    this.getAllPossibleMatchings(this.versionId);
  }

  getTableHeaderNames() {
    const model = new AllPossibleMatchingModel();
    this.displayedColumns = Object.keys(model) as (keyof AllPossibleMatchingModel)[];
  }

  getAllPossibleMatchings(versionId: number) {
    this.tuningsService.findAllPossibleMatchings(versionId).subscribe(response => {
      this.originalAllPossibleMatchings = response;
      this.allPossibleMatchings = response;
    })
  }

  onSearchInput(value: string) {
    if (value && value.trim() !== '') {
      this.allPossibleMatchings = this.originalAllPossibleMatchings.filter(item =>
        item.versionWinOlsReference?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.allPossibleMatchings = [...this.originalAllPossibleMatchings];
    }
  }

  onRefreshTable() {
    this.getAllPossibleMatchings(this.versionId);
  }

  onSort(column: keyof AllPossibleMatchingModel) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.allPossibleMatchings.sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      if (valA === valB) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === 'string' && typeof valB === 'string') {
        return this.sortDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return this.sortDirection === 'asc'
        ? (valA < valB ? -1 : 1)
        : (valA > valB ? -1 : 1);
    });
  }

  downloadVersionFromPureOriginals(id: number, winOls: string) {
    this.filesToDownload.push(id);
    const toastObj = this.toastr.info(`Preparing your ${winOls} file...`, 'Preparing..', {
      disableTimeOut: true, progressBar: true, progressAnimation: 'increasing'
    });

    this.downloadService.downloadFileFromDatabase(id, this.tableName).subscribe(response => {
      const index = this.filesToDownload.indexOf(id);
      this.filesToDownload.splice(index, 1);

      if (response.status === 200) {
        this.toastr.clear(toastObj.toastId);
        window.location.href = response.body.url;
        this.toastr.success(`<div><span>Your file ${winOls} is downloaded</span></div>`, "Success", {
          timeOut: 5000,
          closeButton: false,
        });
      }
    }, error => {
      this.toastr.clear(toastObj.toastId);
      this.toastr.error(`${error.error['message']}`, 'Failed', {
        disableTimeOut: false
      });
    })
  }

  openPureOriginalDetails(id: number) {
    this.router.navigate(['/app/pure-originals/pure-original-details'], {queryParams: {pureOriginalId: id}});
  }

  backToMainTable() {
    this.location.back();
  }

  formatDate(value: any, column: string): string {
    if (value == null || value === '') return '-';

    const dateColumns = ['date', 'dateUploaded', 'dateUpdated', 'dateExported', 'dateMatched', 'versionDateUploaded', 'dateuploaded'];
    if (dateColumns.includes(column)) {
      const parts = value.toString().split('-');
      if (parts.length < 6) return value;

      let year, month, day, hours, minutes, seconds;

      if (parseInt(parts[0], 10) > 12) {
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10) - 1;
        year = parseInt(parts[2], 10);
      } else if (parseInt(parts[1], 10) > 12) {
        month = parseInt(parts[0], 10) - 1;
        day = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
      } else {
        if (column === 'dateuploaded') {
          month = parseInt(parts[0], 10) - 1;
          day = parseInt(parts[1], 10);
        } else {
          day = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10) - 1;
        }
        year = parseInt(parts[2], 10);
      }

      hours = parseInt(parts[3], 10);
      minutes = parseInt(parts[4], 10);
      seconds = parseInt(parts[5], 10);

      const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));

      if (isNaN(utcDate.getTime())) return value;

      const localDay = utcDate.getDate().toString().padStart(2, '0');
      const localMonth = (utcDate.getMonth() + 1).toString().padStart(2, '0');
      const localYear = utcDate.getFullYear();
      const localHours = utcDate.getHours().toString().padStart(2, '0');
      const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
      const localSeconds = utcDate.getSeconds().toString().padStart(2, '0');

      return `${localDay}-${localMonth}-${localYear} ${localHours}:${localMinutes}:${localSeconds}`;
    }

    if (column === 'hitRate' && typeof value === 'number') {
      return (value * 100).toFixed(2) + '%';
    }

    return value.toString();
  }
}
